/**
 * sampleReceipt — ใบกำกับภาษีอย่างย่อ "ตัวอย่าง" สำหรับแนบคำขอ ภ.พ.06
 *
 * ทำไมต้องมี: ภ.พ.06 (คำขออนุมัติใช้เครื่องบันทึกการเก็บเงินเพื่อออกใบกำกับภาษีอย่างย่อ)
 * บังคับให้แนบ "ตัวอย่างใบกำกับภาษีอย่างย่อที่เครื่องนั้นออก" แต่ตอนยื่นร้านยังไม่ได้รับ
 * อนุมัติ จึงออกใบจริงไม่ได้ และเลขประจำเครื่องก็ยังไม่มี (สรรพากรออกให้หลังอนุมัติ)
 * — ถ้าไม่มีปุ่มนี้ ร้านต้อง "ขายจริงแล้วยกเลิก" ซึ่งเผาเลขใบกำกับทิ้งฟรี ๆ 1 ใบ
 *
 * กติกาที่ห้ามพลาด
 *   1. ต้องเรียก buildReceiptDocDefinition ตัวเดียวกับใบจริงเสมอ ห้ามเขียนเทมเพลตที่ 2
 *      ไม่งั้นสิ่งที่ยื่นให้สรรพากรจะไม่ตรงกับสิ่งที่เครื่องพิมพ์ออกมาจริง
 *   2. ข้อมูลร้าน (ชื่อผู้ประกอบการ · ที่อยู่ · เลขผู้เสียภาษี · รหัสสถานประกอบการ) = ของจริง
 *      รายการอาหารและยอด = สมมุติ
 *   3. ห้ามแตะ DB — ไม่สร้างบิล ไม่เดิน abbr_invoice_counters ไม่เข้ารายงานภาษีขาย
 *      (ทั้งไฟล์นี้ทำงานฝั่ง FE ล้วน ไม่มี API call)
 *
 * ดู silver-spoon-api/docs/vat-compliance.md D10/D12
 */

import { buildReceiptDocDefinition, preparePrintConfig, printPdf } from '@/utils/billReceipt';
import {
  isSupported as escposIsSupported,
  printCanvas,
  renderDocDefToCanvas,
  dotsForPaperWidth,
} from '@/utils/escposPrinter';

/** รายการสมมุติชุดเดียวกับพรีวิวหน้าตั้งค่าใบเสร็จ — ยอดรวม 230.00 */
const SAMPLE_ITEMS = [
  { name: 'ก๋วยเตี๋ยวเรือ พิเศษ หมู', unit_price: 50, quantity: 2, total: 100 },
  { name: 'กากหมู', unit_price: 25, quantity: 2, total: 50 },
  {
    name: 'ก๋วยเตี๋ยวเรือ จั๊มโบ เนื้อ',
    unit_price: 80,
    quantity: 1,
    total: 80,
    modifiers: [
      { name: 'เพิ่ม ลูกชิ้นเนื้อ', price: 10 },
      { name: 'เพิ่ม เนื้อสด', price: 10 },
    ],
  },
];

const round2 = (n) => +Number(n || 0).toFixed(2);

/**
 * ประกอบบิลสมมุติให้อยู่ในรูปที่ buildReceiptDocDefinition รับได้
 *
 * ยอดคิดแบบเดียวกับของจริง: รวมรายการ → บวกค่าบริการ → **ถอด** VAT ออกจากยอดรวม
 * (ตรงกับ BillHelper::calculateBillTotals ที่บวกทุกอย่างก่อนแล้วค่อยถอดภาษีจากยอดรวม)
 */
export function buildSampleBill({ shopTax = {}, branchNo = '', device = null, at = new Date() } = {}) {
  const vatRate = Number(shopTax.vat_rate) || 0;
  const serviceRate = 0; // ใบตัวอย่างไม่ผูกกับค่าบริการของสาขา ให้ดูรูปแบบใบเป็นหลัก

  const subtotal = SAMPLE_ITEMS.reduce((s, i) => s + i.total, 0);
  const serviceCharge = round2(subtotal * serviceRate / 100);
  const grandTotal = round2(subtotal + serviceCharge);
  const vat = vatRate > 0 ? round2(grandTotal * vatRate / (100 + vatRate)) : 0;

  const pad = (n) => String(n).padStart(2, '0');
  const yyyymm = `${at.getFullYear()}${pad(at.getMonth() + 1)}`;
  const yyyymmdd = `${yyyymm}${pad(at.getDate())}`;
  // รหัสสาขาจริงถ้าตั้งไว้แล้ว ไม่งั้นใช้ตัวยึดเพื่อให้เห็นรูปแบบเลขครบ
  const code = String(branchNo || '').trim() || '00000';

  return {
    // รูปแบบเลขตรงกับของจริง (TaxInvoiceHelper::generateAbbreviatedNo / BillHelper::generateBillNo)
    // แต่เป็นเลขสมมุติ — counter จริงไม่ถูกแตะ
    bill_no: `${code}-${yyyymmdd}-0001`,
    abbreviated_tax_invoice_no: `INV-${code}-${yyyymm}-000001`,
    payment_status: 'paid',
    // เว้นว่างได้ถ้ายังไม่ได้เลขจากสรรพากร — ซึ่งเป็นสถานะปกติของ "ตอนยื่น ภ.พ.06"
    pos_device_code: device?.rd_device_code || '',
    delivery_platform: '',
    closed_at: at.toISOString(),
    table_name: 'A-12',
    customer_name: '',
    customer_phone: '',
    tax_policy: shopTax.tax_policy || 'no_tax',
    subtotal,
    discount: 0,
    shipping_price: 0,
    service_charge: serviceCharge,
    service_charge_rate: serviceRate,
    total_before_vat: round2(grandTotal - vat),
    vat,
    vat_rate: vatRate,
    grand_total: grandTotal,
    loyalty: null,
    items: SAMPLE_ITEMS.map(i => ({
      name: i.name,
      unit_price: i.unit_price,
      quantity: i.quantity,
      cancelled_quantity: 0,
      subtotal: i.total,
      discount: 0,
      total: i.total,
      modifiers: i.modifiers || [],
      remark: '',
    })),
  };
}

/**
 * พิมพ์ใบตัวอย่างของเครื่องหนึ่งเครื่อง
 *
 * ⚠️ ต้องเดิน **เส้นทางพิมพ์เดียวกับใบจริง** (WebUSB ก่อน ตกไป PDF ถ้าไม่ได้) เพราะจุดประสงค์
 * ทั้งหมดของใบนี้คือ "ให้สรรพากรเห็นสิ่งที่เครื่องนี้พิมพ์ออกมาจริง ๆ" — ถ้าใบตัวอย่างออกจาก
 * PDF dialog แต่ใบจริงออกจากหัวพิมพ์ความร้อน ความกว้าง/ความคมของกระดาษจะคนละเรื่องกัน
 * (ลอกลำดับมาจาก OffcanvasCheckBill.printBill ให้เหมือนกันทุกขั้น)
 *
 * @param {object}   opts.branchConfig  branch_config ของสาขา (ค่าที่กำลังตั้งอยู่บนฟอร์มก็ได้
 *                                      — จะได้เห็นผลของ toggle ที่เพิ่งปรับก่อนกดบันทึก)
 * @param {object}   opts.shopTax       filter.shop_tax
 * @param {object}   opts.device        แถวจาก pos_devices (ใช้แค่ rd_device_code)
 * @param {function} opts.fmt           ตัวจัดรูปแบบเงิน
 * @param {function} opts.t             i18n
 */
export async function printSampleReceipt({ branchConfig, shopTax, device, fmt, t = (s) => s }) {
  // มือถือต้องเปิด popup ตั้งแต่ยังอยู่ในจังหวะที่ผู้ใช้กด (ก่อน await ตัวแรก) ไม่งั้น Safari บล็อก
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const popup = (isMobile && !escposIsSupported()) ? window.open('', '_blank') : null;

  try {
    const configuration = await preparePrintConfig(branchConfig || {});
    const bill = buildSampleBill({
      shopTax,
      branchNo: branchConfig?.branch_no,
      device,
    });

    // QR ไม่พิมพ์บนใบตัวอย่าง — พร้อมเพย์ผูกกับช่องทางรับเงินจริงของสาขา ไม่ใช่เรื่องของ ภ.พ.06
    // และกันคนสแกนใบตัวอย่างแล้วโอนเงินจริง
    const docOpts = {
      bill,
      configuration: { ...configuration, show_qrcode_promptpay: false, show_qrcode_review: false },
      shopTax,
      fmt,
      t,
      sampleNotice: t('Sample for Form Phor.Phor.06 application — not a valid tax document.'),
    };

    // พิมพ์ตรงผ่าน WebUSB — ตัดใต้เนื้อหาพอดี จึงสร้างแบบไม่ต้องถ่วงหน้าให้ยาว
    // ความกว้าง raster = ขนาดกระดาษของเครื่องนี้ (preparePrintConfig เขียนทับให้) เหมือนใบจริงเป๊ะ
    if (escposIsSupported()) {
      try {
        const canvas = await renderDocDefToCanvas(
          buildReceiptDocDefinition({ ...docOpts, padShortPage: false }),
          { widthPx: dotsForPaperWidth(configuration.bill_paper_width) }
        );
        // ใบตัวอย่างไม่ใช่การรับเงิน — ห้ามเตะลิ้นชักเด็ดขาด ต่อให้เครื่องนี้ตั้งว่ามีลิ้นชัก
        await printCanvas(canvas, { cashDrawer: false });
        return;
      } catch (e) {
        // เครื่องพิมพ์ยังไม่ได้เลือก / ถอดสาย / ผู้ใช้กดยกเลิกหน้าต่างเลือกอุปกรณ์
        // ตกไปพิมพ์ผ่าน PDF ต่อ ดีกว่าไม่ได้อะไรเลย (ปุ่มนี้เป็นการกดเองไม่ใช่ auto-print)
        console.warn('WebUSB sample print failed:', e);
      }
    }
    await printPdf(buildReceiptDocDefinition(docOpts), { popup });
  } catch (e) {
    if (popup) popup.close();
    throw e;
  }
}
